import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { getAwards, upsertAwards } from "../api/meeting-api";
import type { AwardsResponse, UpsertAwardsRequest } from "../types/meeting.type";

const STALE_TIME = 30_000;

const awardsKeys = {
  awards: (rfxId: number, evalId: number) =>
    ["awards", rfxId, evalId] as const,
};

interface UseAwardsResult {
  awardsData: AwardsResponse | undefined;
  isLoading: boolean;
  upsertAwards: UseMutationResult<AwardsResponse, Error, UpsertAwardsRequest>;
}

export const useAwards = (
  rfxId: number | undefined,
  evalId: number | undefined
): UseAwardsResult => {
  const queryClient = useQueryClient();

  const query: UseQueryResult<AwardsResponse> = useQuery({
    queryKey: awardsKeys.awards(rfxId!, evalId!),
    queryFn: () => getAwards(rfxId!, evalId!),
    enabled: !!rfxId && !!evalId,
    staleTime: STALE_TIME,
  });

  const upsertAwardsMutation = useMutation({
    mutationFn: (payload: UpsertAwardsRequest) =>
      upsertAwards(rfxId!, evalId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: awardsKeys.awards(rfxId!, evalId!),
      });
    },
  });

  return {
    awardsData: query.data,
    isLoading: query.isLoading,
    upsertAwards: upsertAwardsMutation,
  };
};
